/**
 * Saeulen je Periode, daneben grau derselbe Zeitraum ein Jahr frueher.
 *
 * Aufgelaufene Werte (Saeule mit basis) stehen gestaffelt in einer Saeule:
 * hell der Stand bis zur Periode davor, kraeftig die Periode selbst. So sieht
 * man Monat und Aufgelaufenes zugleich, ohne zweites Diagramm.
 *
 * Gezeichnet in der gemessenen Breite statt skaliert: so bleibt die Schrift
 * gleich gross, ob das Analysefeld schmal oder breit ist. Ist es schmal,
 * steht nur jeder zweite Monatsname darunter.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import { achse, wertMitEinheit } from './format'
import type { Saeulen as SaeulenDaten } from './typen'

/** Runde Achsenschritte, etwa vier Linien zwischen kleinstem und groesstem Wert. */
function skala(min: number, max: number): { lo: number; hi: number; schritte: number[] } {
  const spanne = max - min || Math.abs(max) || 1
  const roh = spanne / 4
  const p = 10 ** Math.floor(Math.log10(roh))
  const schritt = [1, 2, 2.5, 5, 10].map((f) => f * p).find((s) => s >= roh) ?? 10 * p
  const lo = Math.floor(min / schritt) * schritt
  const hi = Math.ceil(max / schritt) * schritt
  const schritte: number[] = []
  for (let x = lo; x <= hi + schritt / 2; x += schritt) schritte.push(Math.abs(x) < schritt / 1e6 ? 0 : x)
  return { lo, hi: hi === lo ? lo + schritt : hi, schritte }
}

const RAND = { links: 64, rechts: 4, oben: 8, unten: 36 }
/** Ungefaehre Groesse der Zahlenbox, fuer ihren Platz ueber oder neben den Saeulen. */
const BOX_BREITE = 180
const BOX_HOEHE = 84
const BOX_UEBERSTAND = 24

export function Saeulen({ daten, hoehe = 220, beschriftung }: { daten: SaeulenDaten; hoehe?: number; beschriftung: string }) {
  const huelle = useRef<HTMLDivElement>(null)
  const [breite, setBreite] = useState(0)
  useLayoutEffect(() => {
    const el = huelle.current
    if (!el) return
    const beobachter = new ResizeObserver(([e]) => setBreite(Math.floor(e!.contentRect.width)))
    beobachter.observe(el)
    return () => beobachter.disconnect()
  }, [])

  const { saeulen, einheit } = daten
  const alle = saeulen.flatMap((s) => [s.wert, s.vorjahr]).filter((w): w is number => w !== null)
  const { lo, hi, schritte } = skala(Math.min(0, ...alle), Math.max(0, ...alle))
  const W = Math.max(breite, 240)
  const fach = (W - RAND.links - RAND.rechts) / saeulen.length
  const y = (w: number) => RAND.oben + (hoehe - RAND.oben - RAND.unten) * (1 - (w - lo) / (hi - lo))
  const mitVorjahr = saeulen.some((s) => s.vorjahr !== null)
  const bw = mitVorjahr ? fach * 0.34 : fach * 0.6
  const jederZweite = fach < 34
  // Der Monat unter dem Zeiger oder Finger: seine Zahlen stehen sofort in einer Box daneben.
  const [aktiv, setAktiv] = useState<number | null>(null)

  function rechteck(von: number, bis: number, links: number, klasse: string, key?: string) {
    const y0 = y(von)
    const y1 = y(bis)
    return <rect key={key} className={klasse} x={links} y={Math.min(y0, y1)} width={bw} height={Math.max(1, Math.abs(y1 - y0))} rx={2} />
  }
  /** Eine Saeule; mit basis gestaffelt: hell bis zur basis, kraeftig von dort bis zum Wert. */
  function balken(w: number | null, basis: number | null | undefined, links: number, klasse: string) {
    if (w === null) return null
    if (basis === undefined || basis === null || basis === 0) return rechteck(0, w, links, klasse)
    return (
      <>
        {rechteck(0, basis, links, `${klasse} saeulen-basis`, 'b')}
        {rechteck(basis, w, links, klasse, 'w')}
      </>
    )
  }

  const a = aktiv === null ? null : saeulen[aktiv]
  const aMitte = aktiv === null ? 0 : RAND.links + fach * aktiv + fach / 2
  /* Die Box steht ueber den Saeulen des Monats, damit sie keine Nachbarn
     verdeckt. Reicht der Platz darueber nicht (hohe Saeule), steht sie
     daneben, auf der Seite zur Mitte hin. */
  const oberkante = a ? y(Math.max(0, a.wert ?? 0, a.vorjahr ?? 0)) : 0
  // Ein Stueck darf die Box ueber die Flaeche hinausragen, in den Abstand zur Legende.
  const darueber = oberkante - 6 - BOX_HOEHE >= -BOX_UEBERSTAND
  const boxStil = darueber
    ? { left: Math.min(Math.max(aMitte, BOX_BREITE / 2), W - BOX_BREITE / 2), top: oberkante - 6, transform: 'translate(-50%, -100%)' }
    : aMitte < W / 2
      ? { left: aMitte + fach / 2 + 6, top: 0 }
      : { right: W - (aMitte - fach / 2) + 6, top: 0 }

  return (
    <div className="saeulen" ref={huelle} onPointerLeave={(e) => e.pointerType === 'mouse' && setAktiv(null)}>
      {breite > 0 && (
        <svg width={W} height={hoehe} role="img" aria-label={beschriftung}>
          {schritte.map((w) => (
            <g key={w}>
              <line className={w === 0 ? 'saeulen-null' : 'saeulen-linie'} x1={RAND.links} x2={W - RAND.rechts} y1={y(w)} y2={y(w)} />
              <text className="saeulen-achse" x={RAND.links - 8} y={y(w) + 4} textAnchor="end">
                {achse(w, einheit)}
              </text>
            </g>
          ))}
          {aktiv !== null && <rect className="saeulen-markierung" x={RAND.links + fach * aktiv} y={RAND.oben} width={fach} height={hoehe - RAND.oben - RAND.unten} rx={4} />}
          {saeulen.map((s, i) => {
            const mitte = RAND.links + fach * i + fach / 2
            const letzte = i === saeulen.length - 1
            const klasse = s.wert !== null && s.wert < 0 ? 'saeulen-negativ' : letzte ? 'saeulen-aktuell' : 'saeulen-wert'
            const name = !jederZweite || (saeulen.length - 1 - i) % 2 === 0
            return (
              <g key={s.periode}>
                {mitVorjahr ? (
                  <>
                    {balken(s.vorjahr, s.basisVorjahr, mitte - bw - 1, 'saeulen-vorjahr')}
                    {balken(s.wert, s.basis, mitte + 1, klasse)}
                  </>
                ) : (
                  balken(s.wert, s.basis, mitte - bw / 2, klasse)
                )}
                {name && (
                  <text className={`saeulen-monat${letzte ? ' aktuell' : ''}`} x={mitte} y={hoehe - 20} textAnchor="middle">
                    {s.name}
                  </text>
                )}
                {(i === 0 || s.name === 'Jan' || s.name === 'Q1') && (
                  <text className="saeulen-achse" x={mitte} y={hoehe - 5} textAnchor="middle">
                    {s.jahr}
                  </text>
                )}
              </g>
            )
          })}
          {/* Treffflaechen ueber die ganze Hoehe: auch ein kleiner Balken laesst sich treffen. */}
          {saeulen.map((s, i) => (
            <rect
              key={`f${s.periode}`}
              className="saeulen-flaeche"
              x={RAND.links + fach * i}
              y={0}
              width={fach}
              height={hoehe}
              onPointerEnter={(e) => e.pointerType === 'mouse' && setAktiv(i)}
              onClick={() => setAktiv(aktiv === i ? null : i)}
            />
          ))}
        </svg>
      )}
      {a && (
        <div className="saeulen-box" role="status" style={boxStil}>
          <div className="saeulen-box-kopf">
            {a.name} {a.jahr}
          </div>
          <div className="saeulen-box-zeile">
            <i className={a.wert !== null && a.wert < 0 ? 'saeulen-negativ' : aktiv === saeulen.length - 1 ? 'saeulen-aktuell' : 'saeulen-wert'} />
            <span>{a.jahr}</span>
            <b>{a.wert === null ? 'kein Wert' : wertMitEinheit(a.wert, einheit)}</b>
          </div>
          {mitVorjahr && (
            <div className="saeulen-box-zeile">
              <i className="saeulen-vorjahr" />
              <span>{a.jahr - 1}</span>
              <b>{a.vorjahr === null ? 'kein Wert' : wertMitEinheit(a.vorjahr, einheit)}</b>
            </div>
          )}
          {a.basis != null && a.wert !== null && (
            <div className="saeulen-box-zeile saeulen-box-davon">
              <span>davon {a.name}</span>
              <b>{wertMitEinheit(a.wert - a.basis, einheit)}</b>
            </div>
          )}
          {a.wert !== null && a.vorjahr !== null && <div className="saeulen-box-fuss">{veraenderung(a.wert, a.vorjahr, einheit)}</div>}
        </div>
      )}
    </div>
  )
}

const zahl = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 })

/** Gegen das Vorjahr in Worten: "18 % mehr als im Vorjahr", bei Quoten in Prozentpunkten. */
function veraenderung(w: number, vj: number, einheit: SaeulenDaten['einheit']): string {
  const d = w - vj
  if (d === 0) return 'Wie im Vorjahr'
  if (einheit === 'prozent') return `${zahl.format(Math.abs(d))} Prozentpunkte ${d > 0 ? 'mehr' : 'weniger'} als im Vorjahr`
  if (vj === 0 || Math.sign(w) !== Math.sign(vj)) return `${wertMitEinheit(Math.abs(d), einheit)} ${d > 0 ? 'mehr' : 'weniger'} als im Vorjahr`
  return `${Math.round((Math.abs(d) / Math.abs(vj)) * 100)} % ${Math.abs(w) > Math.abs(vj) ? 'mehr' : 'weniger'} als im Vorjahr`
}

/** Legende in Worten statt Farbraten. */
export function Legende({ daten, wert, aktuell }: { daten: SaeulenDaten; wert: string; aktuell: string }) {
  const gestaffelt = daten.saeulen.some((s) => s.basis != null && s.basis !== 0)
  const mitVorjahr = daten.saeulen.some((s) => s.vorjahr !== null)
  // Leere Stellen erklaeren, statt sie wie null Euro aussehen zu lassen.
  const ersterWert = daten.saeulen.find((s) => s.wert !== null)
  const ersterVorjahr = daten.saeulen.find((s) => s.vorjahr !== null)
  const vorneLeer = ersterWert && daten.saeulen[0]!.wert === null
  const luecke = mitVorjahr && daten.saeulen[0]!.vorjahr === null && ersterVorjahr
  return (
    <>
      <div className="saeulen-legende">
        <span>
          <i className="saeulen-wert" />
          {wert}
        </span>
        {mitVorjahr && (
          <span>
            <i className="saeulen-vorjahr" />
            ein Jahr davor
          </span>
        )}
        <span>
          <i className="saeulen-aktuell" />
          {aktuell}
        </span>
        {gestaffelt && (
          <span>
            <i className="saeulen-wert saeulen-basis" />
            hell: aufgelaufen bis {daten.saeulen.some((s) => s.name.startsWith('Q')) ? 'zum Vorquartal' : 'zum Vormonat'}
          </span>
        )}
      </div>
      {vorneLeer && (
        <p className="nebensache">
          Werte liegen erst ab {ersterWert.name} {ersterWert.jahr} vor.
        </p>
      )}
      {luecke && (
        <p className="nebensache">
          Vorjahreswerte liegen erst ab {ersterVorjahr.name} {ersterVorjahr.jahr} vor.
        </p>
      )}
    </>
  )
}
